import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { gaIdentify, gaPageView } from "../lib/ga";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";

/**
 * Two jobs, both invisible:
 *  1. send a GA4 page_view with the real document title on every client-side navigation;
 *  2. keep the session fresh — credits, plan and notifications are re-read on every route
 *     change and whenever the tab regains focus, so the user never has to hard-refresh.
 */
export default function RouteTracker() {
  const { pathname, search } = useLocation();
  const { user, refresh } = useAuth();
  const { locale } = useI18n();
  const lastRefresh = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      gaPageView({ path: pathname + search, title: document.title, locale });
    }, 220);
    return () => clearTimeout(t);
  }, [pathname, search, locale]);

  useEffect(() => {
    gaIdentify(user && user !== false ? user : null);
  }, [user]);

  // route change → refresh the session (throttled so fast navigation never floods the API)
  useEffect(() => {
    if (!user || user === false) return;
    const now = Date.now();
    if (now - lastRefresh.current < 4000) return;
    lastRefresh.current = now;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // tab regains focus → the balance may have changed in another tab or by a worker
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      if (!user || user === false) return;
      if (Date.now() - lastRefresh.current < 4000) return;
      lastRefresh.current = Date.now();
      refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user, refresh]);

  return null;
}
